import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AdminDashboardData {
  generated_at: string;
  period_days: number;
  volume: {
    active_companies: number;
    active_company_members: number;
    active_family_owners: number;
    total_patients: number;
    patients_company_linked: number;
    patients_family_only: number;
    active_observers: number;
    active_caregivers: number;
  };
  lifetime: {
    avg_account_age_days: number;
    buckets: { lt30: number; '30_90': number; '90_180': number; gt180: number };
  };
  adoption: {
    total_patients: number;
    without_plan: number;
    without_plan_pct: number;
    without_template: number;
    without_template_pct: number;
    without_plan_list: Array<{ patient_id: string; nickname: string; owner_email: string; created_at: string }>;
    without_template_list: Array<{ patient_id: string; nickname: string; owner_email: string; created_at: string }>;
  };
  notifications: {
    in_app_total: number;
    in_app_prev: number;
    delta_pct: number;
    push_ok: number;
    push_failed: number;
    push_success_rate: number | null;
  };
  engagement: {
    dau: number;
    wau: number;
    mau: number;
    features: Array<{
      key: string;
      current: number;
      previous: number;
      delta_pct: number;
      series?: Array<{ date: string; count: number }>;
    }>;
  };
  revenue: {
    active_subscriptions: number;
    mrr_cents: number;
    churned_in_period: number;
    free_overflow_count: number;
    free_within_count: number;
    free_overflow_list: Array<{ user_id: string; email: string; shifts: number }>;
    free_within_list: Array<{ user_id: string; email: string; shifts: number }>;
    active_subscriptions_list: Array<{
      email: string;
      status: string;
      unit_amount: number | null;
      currency: string | null;
      quantity: number | null;
      interval: string | null;
      current_period_end: string | null;
    }>;
  };
}

export function useAdminDashboard(periodDays: number) {
  return useQuery({
    queryKey: ['admin-dashboard', periodDays],
    queryFn: async (): Promise<AdminDashboardData> => {
      const { data, error } = await supabase.functions.invoke('generate-admin-dashboard', {
        body: { period_days: periodDays },
      });
      if (error) throw error;
      return data as AdminDashboardData;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export type StripeSyncMode = 'incremental' | 'full' | 'single';

export interface StripeSyncResult {
  ok: boolean;
  mode: StripeSyncMode;
  processed: number;
  upserts: number;
  errors: Array<{ id: string; err: string }>;
  cursor: number | null;
}

export function useSyncStripeSubscriptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input?: { mode?: StripeSyncMode; subscription_id?: string },
    ): Promise<StripeSyncResult> => {
      const { data, error } = await supabase.functions.invoke('sync-stripe-subscriptions', {
        body: input ?? { mode: 'incremental' },
      });
      if (error) throw error;
      return data as StripeSyncResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
  });
}
